<template>
	<form @submit.prevent="handleSubmit" class="form" target="_top">
		<div class="input-group">
			<label for="keyword" class="form-label visually-hidden">Keyword</label>
			<input
				type="text"
				v-model="keywordsTyped"
				class="form-control"
                :class="{ 'is-live-keywords': live }"
				id="keyword"
				placeholder="Search by keyword"
			/>
            <i v-if="live" class="fa-regular fa-search"></i>
		</div>

		<!-- Filter by Interest -->
		<div class="filter-group js-filter-group" :class="{ open: openDropdown === 'interests' }">
			<div class="filter-group__dropdown">
				<button class="btn filter-group__button js-dropdown-btn" type="button" @click="toggleDropdown('interests')">
					<div>
						<span class="me-1 fw-bold">Filter by Interest</span>
                        <span v-if="selectedInterests.length > 0" class="filter-group__count fw-bold">
                            ({{ selectedInterests.length }})
                        </span>
					</div>
					<div class="filter-group__icon">
						<i class="fa-regular fa-angle-down"></i>
					</div>
				</button>

				<div class="filter-group__menu js-dropdown-menu" :class="{ active: openDropdown === 'interests' }">
					<div class="filter-group__checkboxes">
						<div v-for="interest in interests" :key="interest" class="form-check">
							<input
								class="form-check-input"
								type="checkbox"
								:id="`interest-${interest}`"
								:value="interest"
								v-model="selectedInterests"
							/>
							<label :for="`interest-${interest}`" class="form-check-label">{{ interest }}</label>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Filter by Type -->
		<div class="filter-group js-filter-group" :class="{ open: openDropdown === 'types' }">
			<div class="filter-group__dropdown">
				<button class="btn filter-group__button js-dropdown-btn" type="button" @click="toggleDropdown('types')">
					<div>
						<span class="me-1 fw-bold">Filter by Program Type</span>
                        <span v-if="selectedTypes.length > 0" class="filter-group__count fw-bold">
                            ({{ selectedTypes.length }})
                        </span>
					</div>
					<div class="filter-group__icon"><i class="fa-regular fa-angle-down"></i></div>
				</button>
				<div class="filter-group__menu js-dropdown-menu" :class="{ active: openDropdown === 'types' }">
					<div class="filter-group__checkboxes">
						<div v-for="type in types" :key="type" class="form-check">
							<input
								class="form-check-input"
								type="checkbox"
								:id="type"
								:value="type"
								v-model="selectedTypes"
							/>
							<label :for="type" class="form-check-label">{{ type }}</label>
						</div>
					</div>
				</div>
			</div>
		</div>

        <button type="submit" class="btn btn-primary submit-btn" v-if="!live">Submit</button>

        <div v-if="hasSelections && live" class="js-selected-tags">
             <span v-for="filter in selectedFilters" :key="filter" class="filter-group__pill">
                {{ filter }}
                <button
                    type="button"
                    class="filter-group__pill-remove"
                    aria-label="Remove"
                    @click="removeFilter(filter)"
                >
                    <i class="fa-solid fa-xmark-large"></i>
                </button>
            </span>

            <a href="#" @click.prevent="clearAllFilters" class="js-clear-all filter-group__clear">Clear All</a>
        </div>

	</form>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useProgramStore } from '@ProgramList/stores/programStore';

const interests = ref<string[]>([]);
const types = ref<string[]>([]);

const props = defineProps<{ live?: boolean }>();

const store = useProgramStore();
const {
	keyword: storeKeyword,
	interests: storeInterests,
	types: storeTypes
} = storeToRefs(store);

// Local state for non-live mode
const localKeyword = ref('');
const localInterests = ref<string[]>([]);
const localTypes = ref<string[]>([]);

// Bound computed values
const keywordsTyped = computed({
	get: () => props.live ? storeKeyword.value : localKeyword.value,
	set: val => props.live ? storeKeyword.value = val : localKeyword.value = val
});
const selectedInterests = computed({
	get: () => props.live ? storeInterests.value : localInterests.value,
	set: val => props.live ? storeInterests.value = val : localInterests.value = val
});
const selectedTypes = computed({
	get: () => props.live ? storeTypes.value : localTypes.value,
	set: val => props.live ? storeTypes.value = val : localTypes.value = val
});

const route = useRoute();
const router = useRouter();

function updateQueryParams() {
    const query = {
        keyword: keywordsTyped.value || '',
        interests: selectedInterests.value.join(','),
        types: selectedTypes.value.join(','),
    };

    router.push({ path: route.path, query });
}

// If in live mode (majors & programs page only), watch for changes and update the store filters
if (props.live) {
	watch([keywordsTyped, selectedInterests, selectedTypes], ([k, i, t]) => {
		store.setFilters({ keyword: k, interests: i, types: t });
        updateQueryParams();
	});
}

// values sent as query parameters to the main app page
const emit = defineEmits<{
	(e: 'submit', payload: { keyword: string; interests: string[]; types: string[] }): void;
}>();

const handleSubmit = () => {
	const payload = {
		keyword: localKeyword.value,
		interests: localInterests.value,
		types: localTypes.value,
	};
	emit('submit', payload);
};

// Pill values (combined list)
const selectedFilters = computed(() => [...selectedInterests.value, ...selectedTypes.value]);

// Check to see if there is a least one selected checkbox
const hasSelections = computed(() => {
    return keywordsTyped.value.length > 0 || selectedInterests.value.length > 0 || selectedTypes.value.length > 0;
});

// Remove individual filter
const removeFilter = (value: string) => {
    selectedInterests.value = selectedInterests.value.filter(v => v !== value);
    selectedTypes.value = selectedTypes.value.filter(v => v !== value);
};

// Clear all filters
const clearAllFilters = () => {
    keywordsTyped.value = '';
    selectedInterests.value = [];
    selectedTypes.value = [];
    updateQueryParams();
};

const openDropdown = ref<'interests' | 'types' | null>(null);

// toggle filter dropdowns
function toggleDropdown(type: 'interests' | 'types') {
    openDropdown.value = openDropdown.value === type ? null : type;
}

function closeDropdownsOnClickOutside(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (!target.closest('.js-filter-group')) {
        openDropdown.value = null;
    }
}

onMounted(() => {
    document.addEventListener('click', closeDropdownsOnClickOutside);

    const appEl = document.querySelector<HTMLElement>('#app[data-interests-path]');
    const jsonPath = appEl?.getAttribute('data-interests-path');

    (async () => {
        const appEl = document.querySelector<HTMLElement>('#app[data-interests-path]');
        const jsonPath = appEl?.getAttribute('data-interests-path');

        if (jsonPath) {
            try {
                const response = await fetch(jsonPath);
                if (!response.ok) throw new Error('Failed to fetch JSON');

                const data = await response.json();
                interests.value = Array.isArray(data.interests) ? data.interests : [];
                types.value = Array.isArray(data.types) ? data.types : [];
            } catch (error) {
                console.error('Error loading interests/types:', error);
            }
        }
    })();
});

onUnmounted(() => {
    document.removeEventListener('click', closeDropdownsOnClickOutside);
});

</script>

<style scoped>
.form {
	display: flex;
	gap: 1.5rem;
    max-width: 65rem;
    margin: auto;
    flex-wrap: wrap;
}
@media (max-width: 991px) {
    .form {
        flex-direction: column;
    }
}
.input-group,
.filter-group {
	flex: 1;
    margin-bottom: 0;
}
label {
	display: flex;
	flex-direction: column;
}
input.form-control {
    height: 51px;
}
.js-selected-tags {
    width: 100%;
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-wrap: wrap;
}
.submit-btn {
    height: 51px;
    padding-top: 0;
    padding-bottom: 0;
    width: fit-content;
    margin: auto;
}
input, .filter-group__dropdown, .filter-group__menu.active {
    border-width: 2px;
    font-size: 18px;
}
#keyword::placeholder  {
    color: #757575 !important;
}
#keyword:focus::placeholder  {
    color: black !important;
}
</style>